import { useMemo } from 'react'
import { BarChart3, TrendingUp, TrendingDown } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardDescription } from '../UI'

const UsageChart = ({ history, loading }) => {
  // Process history into daily usage for last 14 days
  const chartData = useMemo(() => {
    if (!history?.length) return []

    const now = new Date()
    const days = 14
    const dailyUsage = {}

    // Initialize last 14 days
    for (let i = 0; i < days; i++) {
      const date = new Date(now)
      date.setDate(date.getDate() - i)
      const key = date.toISOString().split('T')[0]
      dailyUsage[key] = { date: key, total: 0, services: {} }
    }

    // Aggregate history into daily buckets
    history.forEach(item => {
      if (item.type !== 'deduction' || !item.date) return
      
      const date = item.date instanceof Date ? item.date : new Date(item.date)
      const key = date.toISOString().split('T')[0]
      
      if (dailyUsage[key]) {
        dailyUsage[key].total += item.amount || 0
        const service = item.service || 'other'
        dailyUsage[key].services[service] = (dailyUsage[key].services[service] || 0) + (item.amount || 0)
      }
    })

    // Convert to array and sort by date
    return Object.values(dailyUsage)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
  }, [history])

  // Calculate stats
  const stats = useMemo(() => {
    if (!chartData.length) return { total: 0, average: 0, trend: 0 }

    const total = chartData.reduce((sum, day) => sum + day.total, 0)
    const average = Math.round(total / chartData.length)

    // Calculate trend (compare last 7 days to previous 7 days)
    const recent = chartData.slice(-7).reduce((sum, day) => sum + day.total, 0)
    const previous = chartData.slice(0, 7).reduce((sum, day) => sum + day.total, 0)
    const trend = previous > 0 ? Math.round(((recent - previous) / previous) * 100) : 0

    return { total, average, trend }
  }, [chartData])

  // Find max value for scaling
  const maxValue = useMemo(() => {
    if (!chartData.length) return 100
    const max = Math.max(...chartData.map(d => d.total))
    return max > 0 ? max : 100
  }, [chartData])

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })
  }

  if (loading) {
    return (
      <Card padding="lg">
        <div className="animate-pulse">
          <div className="h-6 bg-dark-700 rounded w-32 mb-2" />
          <div className="h-4 bg-dark-700 rounded w-48 mb-6" />
          <div className="flex items-end gap-1 h-32">
            {[...Array(14)].map((_, i) => (
              <div 
                key={i} 
                className="flex-1 bg-dark-700 rounded-t"
                style={{ height: `${Math.random() * 100}%` }}
              />
            ))}
          </div>
        </div>
      </Card>
    )
  }

  const hasData = chartData.some(d => d.total > 0)

  return (
    <Card padding="lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Usage History</CardTitle>
            <CardDescription>Credits used over the last 14 days</CardDescription>
          </div>
          {hasData && stats.trend !== 0 && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-sm ${
              stats.trend > 0 
                ? 'bg-red-500/10 text-red-400' 
                : 'bg-green-500/10 text-green-400'
            }`}>
              {stats.trend > 0 ? (
                <TrendingUp className="w-4 h-4" />
              ) : (
                <TrendingDown className="w-4 h-4" />
              )}
              <span>{Math.abs(stats.trend)}%</span>
            </div>
          )}
        </div>
      </CardHeader>

      {!hasData ? (
        <div className="h-40 flex flex-col items-center justify-center border border-dashed border-dark-700 rounded-xl">
          <BarChart3 className="w-8 h-8 text-dark-600 mb-2" />
          <p className="text-dark-500 text-sm">No usage data yet</p>
          <p className="text-dark-600 text-xs mt-1">Start using credits to see your history</p>
        </div>
      ) : (
        <>
          {/* Stats Row */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="p-3 bg-dark-800/50 rounded-lg">
              <p className="text-dark-500 text-xs mb-1">Total (14 days)</p>
              <p className="text-lg font-semibold">{stats.total.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-dark-800/50 rounded-lg">
              <p className="text-dark-500 text-xs mb-1">Daily Average</p>
              <p className="text-lg font-semibold">{stats.average.toLocaleString()}</p>
            </div>
          </div>

          {/* Bar Chart */}
          <div className="relative">
            <div className="flex items-end gap-1 h-32">
              {chartData.map((day, index) => {
                const height = (day.total / maxValue) * 100
                return (
                  <div 
                    key={day.date}
                    className="flex-1 group relative"
                  >
                    {/* Bar */}
                    <div 
                      className={`w-full bg-gradient-to-t from-accent-600 to-accent-400 rounded-t transition-all duration-300 hover:from-accent-500 hover:to-accent-300 ${
                        day.total === 0 ? 'bg-dark-700' : ''
                      }`}
                      style={{ 
                        height: day.total > 0 ? `${Math.max(height, 4)}%` : '2px',
                        minHeight: day.total > 0 ? '4px' : '2px'
                      }}
                    />
                    
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                      <div className="bg-dark-800 border border-dark-600 rounded-lg px-2 py-1 text-xs whitespace-nowrap shadow-lg">
                        <p className="text-dark-300">{formatDate(day.date)}</p>
                        <p className="font-semibold text-white">{day.total.toLocaleString()} credits</p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* X-axis labels (show every 3rd) */}
            <div className="flex gap-1 mt-2">
              {chartData.map((day, index) => (
                <div key={day.date} className="flex-1 text-center">
                  {index % 4 === 0 && (
                    <span className="text-[10px] text-dark-500">
                      {new Date(day.date).getDate()}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </Card>
  )
}

export default UsageChart
